import buttonSocial from '@assets/button-social.svg';
import navigationArrow from '@assets/icons/icon-arrow.svg';
import NavigationItem from '@components/NavigationItem';
import NavigationOptionItem from '@components/NavigationOptionItem';
import SocialButtonItem from '@components/SocialButtonItem';
import data from '@data/data';
import useQuestionnaireStore from '@store/questionnaire';
import useStore from '@store/store';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { hash } from 'utilities/helper';

const Navigation: React.FC = () => {
  const { displayEndResultsStore } = useQuestionnaireStore();
  const { showSocialButtons, setShowSocialButtons, page } = useStore();
  const [showMobileNavigation, setShowMobileNavigation] = useState(false);

  const [isFadingIn, setIsFadingIn] = useState(false);
  const [isVisible, setIsVisible] = useState(showSocialButtons);

  const items = Object.values(data);

  // Social buttons
  const socialButtons: readonly string[] = ['email', 'x', 'linkedin'];
  const handleClick = (): void => {
    setShowSocialButtons();
  };

  useEffect(() => {
    if (showSocialButtons) {
      // If showSocialButtons becomes true, make the element visible and start fading in
      setIsVisible(true);
      setTimeout(() => setIsFadingIn(true), 50); // Start fading in after a small delay
    } else {
      // If showSocialButtons becomes false, start fading out
      setIsFadingIn(false);

      // Remove the element from the DOM after the fade-out transition ends (200ms in this case)
      setTimeout(() => setIsVisible(false), 200); // Match this with transition duration
    }
  }, [showSocialButtons]);

  return (
    <nav className="z-[51] fixed top-0 left-0 right-0 w-full bg-white">
      <div className="relative max-w-outerGrid mx-auto">
        <ul className="gap-4 justify-around py-4 max-w-innerGrid mx-auto hidden md:flex">
          {items.map((item) => {
            // Don't show the results page if the user hasn't reached the end of the survey
            if (!displayEndResultsStore && item.href === '#results')
              return null;

            return <NavigationItem {...item} key={hash(item)} />;
          })}
        </ul>
        <div className="block md:hidden mx-auto text-red my-4 text-sm text-center bg-white">
          <button
            onClick={() => setShowMobileNavigation(!showMobileNavigation)}
            className="bg-white flex flex-row justify-center text-sm font-lightNormal gap-4 mx-auto"
          >
            <em className="not-italic capitalize">
              {page ? page.toLowerCase().replace('_', ' ') : ''}
            </em>
            <img src={navigationArrow} alt="Expand" />
          </button>
          {showMobileNavigation && (
            <ul className="absolute top-full left-0 right-0 bg-white py-2">
              {items.map((item) => {
                // Don't show the results page if the user hasn't reached the end of the survey
                if (!displayEndResultsStore && item.href === '#results')
                  return null;

                return (
                  <NavigationOptionItem
                    {...item}
                    key={hash(item)}
                    setShowMobileNavigation={setShowMobileNavigation}
                  />
                );
              })}
            </ul>
          )}
        </div>

        <div className="absolute top-0 md:top-1/2 right-4 xl:right-0 md:-translate-y-1/2 hidden lg:flex flex-col md:flex-row">
          {isVisible && (
            <div
              className={classNames(
                'text-left mt-1 md:mt-0 md:mr-1 md:relative order-2 md:order-1 transition-opacity duration-200 ease-in',
                {
                  'opacity-100': isFadingIn,
                  'opacity-0': !isFadingIn,
                }
              )}
            >
              <ul className="flex gap-1 flex-col md:flex-row">
                {socialButtons.map((button) => (
                  <SocialButtonItem type={button} key={hash(button)} />
                ))}
              </ul>
              <em className="font-normal text-xs not-italic text-xxs absolute -top-5 left-0 hidden md:block pt-1">
                Share
              </em>
            </div>
          )}
          <button onClick={handleClick} className="order-1 md:order-2">
            <img src={buttonSocial} alt="Share link on social networks" />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
